import { formatCurrencyAmountWithIssue } from '../../../../../commons/currency/Currency.Functions';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Caption1,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  TableCellLayout,
  TableColumnDefinition,
  Text,
  Tooltip,
  ToggleButton
} from '@fluentui/react-components';
import { SearchBox, Separator } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { bundleIcon, ArrowTrendingDownFilled, ArrowTrendingFilled, DocumentText20Filled, DocumentText20Regular, Add24Regular, Edit24Regular, GroupList20Regular, Info24Regular, LinkAdd24Regular, LinkDismiss24Regular, Tag24Regular } from '@fluentui/react-icons';
import * as ComponentFunctions from './ConceptSponsorList.Functions';
import { useConceptSponsorListStyles } from './ConceptSponsorList.Styles';
import ConceptSponsorFormComponent from '../ConceptSponsorForm/ConceptSponsorForm.Component';
import { ContractConceptCategoryListComponent } from '../../ConceptCategories';
import ConceptValuesFormComponent from '../../ConceptValuesForm/ConceptValuesForm.Component';
import ConceptSponsorDetailsComponent from '../ConceptSponsorDetails/ConceptSponsorDetails.Component';

const ConceptIcon = bundleIcon(DocumentText20Filled, DocumentText20Regular);

const formatCost = (value: number | null | undefined, currency: string | null | undefined): string =>
  Number.isFinite(value) ? formatCurrencyAmountWithIssue(value as number, currency) : '-';

// ── CostCell sub-component ────────────────────────────────
interface CostCellProps {
  value: number | null | undefined;
  currency: string | null | undefined;
  variance: number | null | undefined;
  styles: ReturnType<typeof import('./ConceptSponsorList.Styles').useConceptSponsorListStyles>;
}

const CostCell: React.FC<CostCellProps> = ({ value, currency, variance, styles }) => (
  <div>
    <span className={styles.costValue}>{formatCost(value, currency)}</span>
    {variance !== undefined && variance !== null && variance !== 0 && (
      <div className={styles.costBadgeRow}>
        <Badge
          appearance="tint"
          color={variance > 0 ? 'success' : 'danger'}
          size="small"
          icon={variance > 0 ? <ArrowTrendingFilled /> : <ArrowTrendingDownFilled />}
        >
          {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
        </Badge>
      </div>
    )}
  </div>
);

const ConceptSponsorListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useConceptSponsorListStyles();
  const [searchText, setSearchText] = useState<string>('');
  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState<boolean>(false);
  const [selectedConceptId, setSelectedConceptId] = useState<number | undefined>();
  const [detailConceptId, setDetailConceptId] = useState<number | undefined>();
  const [isNewConceptOpen, setIsNewConceptOpen] = useState<boolean>(false);
  const [isGroupedByCategory, setIsGroupedByCategory] = useState<boolean>(false);

  useEffect(() => {
    props.GetAllConcepts();
    props.GetAllConceptSponsors(props.sponsorId);
  }, [props.GetAllConcepts, props.GetAllConceptSponsors, props.sponsorId]);

  useEffect(() => {
    props.GetAllConceptCategories();
  }, [props.GetAllConceptCategories]);

  const normalizedSearch = searchText.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) {
      return props.rows;
    }

    return props.rows.filter((row) => {
      return [
        row.conceptName,
        row.conceptReferenceId,
        row.conceptDescription
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [props.rows, normalizedSearch]);

  interface CategoryGroup {
    key: string;
    categoryId: number | undefined;
    categoryName: string;
    rows: ComponentFunctions.ConceptSponsorListRow[];
  }

  const groupedRows = useMemo((): CategoryGroup[] => {
    const categoryMap = new Map<number, string>(
      props.categories.map(cat => [cat.id, cat.conceptCategoryName || ''])
    );
    const groups = new Map<string, CategoryGroup>();
    for (const row of filteredRows) {
      const key = typeof row.conceptCategoryId === 'number' ? String(row.conceptCategoryId) : '__none__';
      if (!groups.has(key)) {
        const categoryName = typeof row.conceptCategoryId === 'number'
          ? (categoryMap.get(row.conceptCategoryId) || String(row.conceptCategoryId))
          : t('conceptSponsorList.group.uncategorized', 'Sin categoría');
        groups.set(key, { key, categoryId: row.conceptCategoryId, categoryName, rows: [] });
      }
      groups.get(key)!.rows.push(row);
    }
    return [...groups.values()].sort((a, b) => {
      if (typeof a.categoryId !== 'number') return 1;
      if (typeof b.categoryId !== 'number') return -1;
      return a.categoryName.localeCompare(b.categoryName);
    });
  }, [filteredRows, props.categories, t]);

  const columns: TableColumnDefinition<ComponentFunctions.ConceptSponsorListRow>[] = useMemo(
    () => [
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'conceptName',
        compare: (a, b) => a.conceptName.localeCompare(b.conceptName),
        renderHeaderCell: () => t('conceptSponsorList.columns.concept', 'Concepto'),
        renderCell: (item) => (
          <TableCellLayout media={<ConceptIcon />}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className={styles.conceptName}>{item.conceptName}</span>
              {item.conceptDescription && (
                <span className={styles.conceptDesc}>{item.conceptDescription}</span>
              )}
            </div>
          </TableCellLayout>
        )
      }),
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'conceptCode',
        compare: (a, b) => a.conceptCode.localeCompare(b.conceptCode),
        renderHeaderCell: () => t('conceptSponsorList.columns.code', 'Código'),
        renderCell: (item) => <TableCellLayout>{item.conceptCode || '-'}</TableCellLayout>
      }),
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'referenceCost',
        compare: (a, b) => (a.referenceCost ?? 0) - (b.referenceCost ?? 0),
        renderHeaderCell: () => t('conceptSponsorList.columns.referenceCost', 'Costo ref.'),
        renderCell: (item) => (
          <TableCellLayout>
            <CostCell value={item.referenceCost} currency={item.costCurrency} variance={item.referenceCostVariance} styles={styles} />
          </TableCellLayout>
        )
      }),
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'sponsorWeigthedCost',
        compare: (a, b) => (a.sponsorWeigthedCost ?? 0) - (b.sponsorWeigthedCost ?? 0),
        renderHeaderCell: () => t('conceptSponsorList.columns.sponsorCost', 'C. Sponsor'),
        renderCell: (item) => (
          <TableCellLayout>
            <CostCell value={item.sponsorWeigthedCost} currency={item.costCurrency} variance={item.sponsorWeigthedCostVariance} styles={styles} />
          </TableCellLayout>
        )
      }),
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'siteWeigthedCost',
        compare: (a, b) => (a.siteWeigthedCost ?? 0) - (b.siteWeigthedCost ?? 0),
        renderHeaderCell: () => t('conceptSponsorList.columns.siteCost', 'C. Sitio'),
        renderCell: (item) => (
          <TableCellLayout>
            <CostCell value={item.siteWeigthedCost} currency={item.costCurrency} variance={item.siteWeigthedCostVariance} styles={styles} />
          </TableCellLayout>
        )
      }),
      createTableColumn<ComponentFunctions.ConceptSponsorListRow>({
        columnId: 'actions',
        renderHeaderCell: () => t('conceptSponsorList.columns.actions', 'Acciones'),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionCell}>
              <Tooltip content={t('conceptSponsorList.actions.detail', 'Ver detalle')} relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Info24Regular />}
                  onClick={() => setDetailConceptId(item.conceptId)}
                />
              </Tooltip>
              <Tooltip content={t('conceptSponsorList.actions.edit', 'Editar concepto')} relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Edit24Regular />}
                  onClick={() => setSelectedConceptId(item.conceptId)}
                />
              </Tooltip>
              <Tooltip content={t('conceptSponsorList.actions.disassociate', 'Desasociar')} relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<LinkDismiss24Regular />}
                  onClick={() => item.conceptSponsorId && props.DeleteConceptSponsor(item.conceptSponsorId)}
                />
              </Tooltip>
            </div>
          </TableCellLayout>
        )
      })
    ],
    [t]
  );

  const renderGrid = (rows: ComponentFunctions.ConceptSponsorListRow[]) => (
    <div className={styles.gridWrapper}>
      <DataGrid
        items={rows}
        columns={columns}
        sortable
        getRowId={(item) => item.rowKey}
        focusMode="composite"
        columnSizingOptions={{
          conceptName:         { idealWidth: 380, minWidth: 200 },
          conceptCode:         { idealWidth: 140, minWidth: 100 },
          referenceCost:       { idealWidth: 180, minWidth: 130 },
          sponsorWeigthedCost: { idealWidth: 180, minWidth: 130 },
          siteWeigthedCost:    { idealWidth: 180, minWidth: 130 },
          actions:             { idealWidth: 110, minWidth: 110 },
        }}
      >
        <DataGridHeader style={{ backgroundColor: 'var(--colorNeutralBackground3)' }}>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell
                style={{
                  fontWeight: 'var(--fontWeightSemibold)',
                  fontSize: 'var(--fontSizeBase200)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--colorNeutralForeground3)',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                }}
              >
                {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<ComponentFunctions.ConceptSponsorListRow>>
          {({ item, rowId }) => (
            <DataGridRow<ComponentFunctions.ConceptSponsorListRow>
              key={rowId}
              style={{ cursor: 'default' }}
            >
              {({ renderCell }) => (
                <DataGridCell style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                  {renderCell(item)}
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );

  // ── Summary stats ──────────────────────────────────────
  const summary = useMemo(() => {
    const total = filteredRows.length;
    const withSponsorCost = filteredRows.filter(r => r.sponsorWeigthedCost != null).length;
    const withContract = filteredRows.filter(r => r.conceptCategoryId != null).length;
    return { total, withSponsorCost, withContract };
  }, [filteredRows]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.searchContainer}>
          <SearchBox
            placeholder={t('conceptSponsorList.search.placeholder', 'Buscar por concepto')}
            onChange={(_, value) => setSearchText(value || '')}
            onClear={() => setSearchText('')}
            value={searchText}
          />
        </div>
        <div className={styles.toolbarActions}>
          <Button
            appearance="secondary"
            icon={<Add24Regular />}
            onClick={() => setIsNewConceptOpen(true)}
          >
            {t('conceptSponsorList.actions.addConcept', 'Agregar concepto')}
          </Button>
          <Button
            appearance="secondary"
            icon={<Tag24Regular />}
            onClick={() => setIsCategoriesOpen(true)}
          >
            {t('conceptSponsorList.actions.manageCategories', 'Administrar categorías')}
          </Button>
          <ToggleButton
            appearance="secondary"
            icon={<GroupList20Regular />}
            checked={isGroupedByCategory}
            onClick={() => setIsGroupedByCategory(prev => !prev)}
          >
            {t('conceptSponsorList.actions.groupByCategory', 'Agrupar por categoría')}
          </ToggleButton>
          <Button
            appearance="primary"
            icon={<LinkAdd24Regular />}
            onClick={() => setIsSelectorOpen(true)}
          >
            {t('conceptSponsorList.actions.openSelector', 'Seleccionar conceptos')}
          </Button>
        </div>
      </div>

      {/* Summary bar */}
      {!props.isLoading && filteredRows.length > 0 && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.total}</span>
            <span className={styles.summaryLabel}>Conceptos</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.withSponsorCost}</span>
            <span className={styles.summaryLabel}>Con costo sponsor</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{isGroupedByCategory ? groupedRows.length : '—'}</span>
            <span className={styles.summaryLabel}>Categorías</span>
          </div>
        </div>
      )}

      {props.errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {props.isLoading && filteredRows.length === 0 && (
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
          <Caption1>{t('conceptSponsorList.loading', 'Cargando conceptos del sponsor...')}</Caption1>
        </div>
      )}

      {props.isLoading === false && filteredRows.length === 0 && props.errorMessage == null && (
        <div className={styles.emptyState}>
          <DocumentText20Regular className={styles.emptyStateIcon} />
          <Subtitle2>{t('conceptSponsorList.empty', 'No hay conceptos asociados a este sponsor.')}</Subtitle2>
          <Caption1>{t('conceptSponsorList.emptyHint', 'Usá "Seleccionar conceptos" para vincular conceptos negociables.')}</Caption1>
        </div>
      )}

      {props.isLoading === false && filteredRows.length > 0 && (
        isGroupedByCategory ? (
          <Accordion multiple collapsible>
            {groupedRows.map(group => (
              <AccordionItem key={group.key} value={group.key}>
                <AccordionHeader>
                  <div className={styles.accordionHeader}>
                    <Text weight="semibold">{group.categoryName}</Text>
                    <div className={styles.accordionMeta}>
                      <Badge appearance="tint" color="brand" size="small">{group.rows.length} concepto{group.rows.length === 1 ? '' : 's'}</Badge>
                    </div>
                  </div>
                </AccordionHeader>
                <AccordionPanel>
                  {renderGrid(group.rows)}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          renderGrid(filteredRows)
        )
      )}

      {isSelectorOpen && (
        <ConceptSponsorFormComponent
          sponsorId={props.sponsorId}
          isOpen={isSelectorOpen}
          dismissPanel={() => setIsSelectorOpen(false)}
        />
      )}

      <ContractConceptCategoryListComponent
        isOpen={isCategoriesOpen}
        onDismiss={() => setIsCategoriesOpen(false)}
      />

      {selectedConceptId != null && (() => {
        const concept = props.concepts.find((c) => c.id === selectedConceptId);
        return concept ? (
          <ConceptValuesFormComponent
            concept={concept}
            isOpen={true}
            dismissPanel={() => setSelectedConceptId(undefined)}
          />
        ) : null;
      })()}

      <ConceptSponsorDetailsComponent
        concept={props.concepts.find((c) => c.id === detailConceptId)}
        isOpen={detailConceptId != null}
        onDismiss={() => setDetailConceptId(undefined)}
      />

      <ConceptValuesFormComponent
        isOpen={isNewConceptOpen}
        sponsorId={props.sponsorId}
        dismissPanel={() => setIsNewConceptOpen(false)}
        onSuccess={() => props.GetAllConceptSponsors(props.sponsorId)}
      />
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ConceptSponsorListComponent as any);